import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CardModal from "../CardModal";
import { resolveAccess } from "../utils/access";
import { apiUrl, resolveUploadsPath } from "../config/api";
import { useBreakpoint } from "../utils/useMediaQuery";

/*
  Страница Справочник ТМЦ — перенесена из App.js.
  Содержит своё состояние (tmcList, filters и т.д.), модалку и таблицу.
*/
export default function TmcPage() {
  React.useEffect(() => { document.title = 'Справочник ТМЦ'; }, []);
  const isCompact = useBreakpoint("(max-width: 960px)");
  const [tmcList, setTmcList] = useState([]);
  const [filters, setFilters] = useState({
    code: "", name: "", group: "", category: "", unit: "",
    price: "", supplier: "", comment: "", amortization: ""
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedId, setHighlightedId] = useState(null);
  const [groups, setGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const token = typeof window !== 'undefined' ? (localStorage.getItem('authToken') || '') : '';
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

  const stickyRef = useRef(null);

  const loadTmc = useCallback(() => {
    fetch(apiUrl('/api/tmc'), { headers: authHeaders })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data)) {
          setTmcList(data);
        } else {
          setTmcList([]);
        }
      })
      .catch(() => setTmcList([]));
  }, [authHeaders]);

  const loadDicts = useCallback(() => {
    fetch(apiUrl('/api/tmc/groups'), { headers: authHeaders })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => setGroups(ok && Array.isArray(data) ? data : []))
      .catch(() => setGroups([]));
    fetch(apiUrl('/api/tmc/categories'), { headers: authHeaders })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => setCategories(ok && Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, [authHeaders]);

  useEffect(() => {
    loadTmc();
    loadDicts();
  }, [loadTmc, loadDicts]);

  const maxCodeLength = tmcList.reduce((m, i) => Math.max(m, String(i.code ?? "").length), 2);

  // Локальные стили ячеек (как было)
  const thStyle = {
    padding: "4px 4px 2px 4px",
    border: "1px solid #ddd",
    background: "#fafafa",
    fontWeight: "bold",
    textAlign: "center",
    overflow: "hidden",
    whiteSpace: "nowrap"
  };
  const codeThStyle = { ...thStyle, minWidth: `${maxCodeLength + 2}ch`, width: `${maxCodeLength + 2}ch` };
  const thNameStyle = { ...thStyle, minWidth: "250px", maxWidth: "600px", whiteSpace: "normal", textAlign: "left" };
  const tdCodeStyle = { padding: "4px", border: "1px solid #eee", textAlign: "center", fontSize: "15px", maxWidth: "60px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const tdNameStyle = { padding: "4px", border: "1px solid #eee", textAlign: "left", fontSize: "15px", minWidth: "250px", maxWidth: "600px", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.15 };
  const tdStyle = { padding: "4px", border: "1px solid #eee", textAlign: "center", fontSize: "15px", maxWidth: "210px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
  const inputStyle = { padding: "2px 4px", fontSize: "13px", borderRadius: "3px", border: "1px solid #ccc", marginTop: "2px", marginBottom: "2px", width: "90%" };
  const journalLinkStyle = { color: "#1d4ed8", textDecoration: "underline", fontWeight: 600 };

  function getGroupName(id) { const f = groups.find(g => String(g.id) === String(id)); return f ? f.name : id; }
  function getCategoryName(id) { const f = categories.find(c => String(c.id) === String(id)); return f ? f.name : id; }

  const fill = (key, value, e) => {
    if (e && e.ctrlKey) {
      e.stopPropagation();
      setFilters((f) => ({ ...f, [key]: String(value ?? '') }));
      return true;
    }
    return false;
  };

  const filtered = tmcList.filter(item =>
    (filters.code === "" || String(item.code ?? "").toLowerCase().includes(filters.code.toLowerCase())) &&
    (filters.name === "" || String(item.name || "").toLowerCase().includes(filters.name.toLowerCase())) &&
    (filters.group === "" || String(getGroupName(item.group_id) || "").toLowerCase().includes(filters.group.toLowerCase())) &&
    (filters.category === "" || String(getCategoryName(item.category_id) || "").toLowerCase().includes(filters.category.toLowerCase())) &&
    (filters.unit === "" || String(item.unit || "").toLowerCase().includes(filters.unit.toLowerCase())) &&
    (filters.price === "" || String(item.price ?? "").toLowerCase().includes(filters.price.toLowerCase())) &&
    (filters.supplier === "" || String(item.supplier_link || "").toLowerCase().includes(filters.supplier.toLowerCase())) &&
    (filters.comment === "" || String(item.comment || "").toLowerCase().includes(filters.comment.toLowerCase())) &&
    (filters.amortization === "" || String(item.amortization_period ?? "").toLowerCase().includes(filters.amortization.toLowerCase()))
  );

  let authUser = null;
  try { authUser = JSON.parse(localStorage.getItem('authUser') || 'null'); } catch {}
  const access = resolveAccess(authUser);
  const canEditTmc = access.canEdit.tmc;

  const handleAddNew = useCallback(() => {
    setSelectedItem({});
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedItem(null);
  }, []);

  const handleRemovePosition = useCallback((id) => {
    if (!id) return;
    if (!window.confirm('Удалить позицию ТМЦ без возврата?')) return;
    fetch(apiUrl(`/api/tmc/${encodeURIComponent(id)}`), {
      method: 'DELETE',
      headers: authHeaders
    })
      .then(res => res.json().then(data => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          alert('Ошибка удаления: ' + (data?.error || 'server'));
          return;
        }
        setTmcList(list => list.filter(item => item.id !== id));
      })
      .catch(() => alert('Сбой сети при удалении'));
  }, [authHeaders]);

  const handleSave = useCallback((savedItem) => {
    if (savedItem?.id) {
      setHighlightedId(savedItem.id);
    }
    loadTmc();
  }, [loadTmc]);

  return (
    <div style={{ width: "100%", maxWidth: "100vw", padding: isCompact ? "12px 8px" : "12px" }}>
      {/* ЕДИНЫЙ липкий контейнер для заголовка и тулбара — без зазора */}
      <div
        ref={stickyRef}
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          background: "#fafcff",
          margin: 0,
          borderBottom: "1px solid #e6e8ef",
          boxShadow: "0 1px 8px #0001",
        }}
      >
        <h2 style={{
          textAlign: "center",
          margin: 0,
          fontWeight: "bold",
          fontSize: isCompact ? "1.05rem" : "1.2rem",
          letterSpacing: "0.5px",
          padding: "8px 0 6px 0"
        }}>
          Справочник ТМЦ
        </h2>

        <div
          style={{
            padding: isCompact ? "10px 10px" : "10px 16px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: isCompact ? "center" : "flex-end",
            gap: 12,
            borderTop: "1px solid #e6e8ef",
            background: "#fafcff",
          }}
        >
          {canEditTmc && (
          <button
            style={{ fontSize: 16, background: "#e7f6d4", border: "1px solid #8bc34a", padding: "6px 16px", borderRadius: 4, cursor: "pointer" }}
            onClick={handleAddNew}
          >➕ Добавить позицию</button>
          )}
        </div>
      </div>

      <div className="table-scroll" style={{ marginTop: 12 }}>
        <table
          className="table-sticky"
          style={{
            width: "100%",
            minWidth: isCompact ? "960px" : "1100px",
            background: "#fff",
            fontSize: "15px",
            tableLayout: "fixed",
            borderCollapse: "separate",
            borderSpacing: 0
          }}
        >
        <colgroup>
          <col style={{ width: "60px" }} />
          <col style={{ width: "350px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "210px" }} />
          <col style={{ width: "75px" }} />
          <col style={{ width: "70px" }} />
          <col style={{ width: "85px" }} />
          <col style={{ width: "130px" }} />
          <col style={{ width: "55px" }} />
          <col style={{ width: "90px" }} />
          <col style={{ width: "70px" }} />
        </colgroup>

        <thead>
          <tr>
            <th style={codeThStyle}>Код<br />
              <input style={inputStyle}
                value={filters.code}
                onChange={e => setFilters(f => ({ ...f, code: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thNameStyle}>Наименование<br />
              <input style={inputStyle}
                value={filters.name}
                onChange={e => setFilters(f => ({ ...f, name: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Группа<br />
              <input style={inputStyle}
                value={filters.group}
                onChange={e => setFilters(f => ({ ...f, group: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Категория<br />
              <input style={inputStyle}
                value={filters.category}
                onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Ед. изм.<br />
              <input style={inputStyle}
                value={filters.unit}
                onChange={e => setFilters(f => ({ ...f, unit: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Цена<br />
              <input style={inputStyle}
                value={filters.price}
                onChange={e => setFilters(f => ({ ...f, price: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Поставщик<br />
              <input style={inputStyle}
                value={filters.supplier}
                onChange={e => setFilters(f => ({ ...f, supplier: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Комментарий<br />
              <input style={inputStyle}
                value={filters.comment}
                onChange={e => setFilters(f => ({ ...f, comment: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}>Фото</th>
            <th style={thStyle}>Амортизация (мес.)<br />
              <input style={inputStyle}
                value={filters.amortization}
                onChange={e => setFilters(f => ({ ...f, amortization: e.target.value }))}
                placeholder="Поиск..." />
            </th>
            <th style={thStyle}></th>
          </tr>
        </thead>

        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={11} style={{ textAlign: "center", padding: 16, color: "#888" }}>Нет данных</td>
            </tr>
          ) : filtered.map(item => (
            <tr
              key={item.id}
              style={{
                borderBottom: "1px solid #eee",
                cursor: "pointer",
                background: (highlightedId === item.id) ? "#e9f2fa" : undefined
              }}
              onClick={() => setHighlightedId(item.id)}
              onDoubleClick={() => { window.open(`${window.location.origin}/tmc/${item.id}`, '_blank', 'noopener'); }}
            >
              <td style={tdCodeStyle} onClick={(e)=>fill('code', item.code, e)}>
                <Link to={`/tmc/${item.id}`} style={journalLinkStyle}>
                  {item.code}
                </Link>
              </td>
              <td style={tdNameStyle} onClick={(e)=>fill('name', item.name, e)}>{item.name}</td>
              <td style={tdStyle} onClick={(e)=>fill('group', getGroupName(item.group_id), e)}>{getGroupName(item.group_id)}</td>
              <td style={tdStyle} onClick={(e)=>fill('category', getCategoryName(item.category_id), e)}>{getCategoryName(item.category_id)}</td>
              <td style={tdStyle} onClick={(e)=>fill('unit', item.unit, e)}>{item.unit}</td>
              <td style={tdStyle}>
                {item.price !== undefined && item.price !== "" && !isNaN(Number(item.price))
                  ? Number(item.price).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                  : ""}
              </td>
              <td style={tdStyle} onClick={(e)=>fill('supplier', item.supplier_link, e)}>
                {item.supplier_link
                  ? <a href={item.supplier_link} target="_blank" rel="noopener noreferrer" style={{ color: "#3a27c5", textDecoration: "underline" }}>Ссылка</a>
                  : "-"}
              </td>
              <td style={tdStyle} onClick={(e)=>fill('comment', item.comment, e)}>{item.comment}</td>
              <td style={tdStyle}>
                {item.photo_url
                  ? <img src={resolveUploadsPath(item.photo_url)} alt="Фото" width={40} />
                  : "-"}
              </td>
              <td style={tdStyle} onClick={(e)=>fill('amortization', item.amortization_period, e)}>{item.amortization_period ?? "-"}</td>
              <td style={tdStyle}>
                {canEditTmc && (
                  <button
                    style={{ border: 'none', background: 'none', color: '#c00', fontSize: '18px', cursor: 'pointer' }}
                    onClick={e => { e.stopPropagation(); handleRemovePosition(item.id); }}
                    title="Удалить позицию"
                  >🗑️</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>

      <CardModal
        open={modalOpen}
        onClose={handleModalClose}
        item={selectedItem}
        onSave={handleSave}
        isAdmin={canEditTmc}
        tmcList={tmcList}
        authHeaders={authHeaders}
      />
    </div>
  );
}